import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BrowserCompatWrapper } from '@/components/ui/BrowserCompatWrapper';
import { useDeleteMunicipalList } from '../hooks/useMunicipalListMutations';
import { countNotified } from '../utils/status-transitions.utils';
import { preRegistrationRoutes } from '../utils/routes.utils';
import type { MunicipalListDetail } from '../types/pre-registration.types';

interface DeleteMunicipalListButtonProps {
  list: MunicipalListDetail;
}

const notifiedReason = (notified: number): string =>
  notified === 1
    ? 'No se puede eliminar: 1 alojamiento ya fue notificado a Fiscalización'
    : `No se puede eliminar: ${notified} alojamientos ya fueron notificados a Fiscalización`;

const confirmText = (list: MunicipalListDetail): string => {
  const rows = list.accommodations.length;
  const municipality = list.municipality.name;
  if (rows === 0) {
    return `Se eliminará el listado de ${municipality}. Esta acción no se puede deshacer.`;
  }
  const rowsText = rows === 1 ? 'su alojamiento' : `sus ${rows} alojamientos`;
  return `Se eliminarán el listado de ${municipality} y ${rowsText} (incluidos los eliminados). Esta acción no se puede deshacer.`;
};

// Deletes the list with all its rows; blocked once some row (deleted ones included) was notified
export const DeleteMunicipalListButton: React.FC<DeleteMunicipalListButtonProps> = ({ list }) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const deleteMutation = useDeleteMunicipalList();
  const notified = countNotified(list.accommodations);

  const confirm = () => {
    setConfirmOpen(false);
    deleteMutation.mutate(list.id, {
      onSuccess: () => navigate(preRegistrationRoutes.municipalLists),
    });
  };

  const button = (
    <Button
      variant="outline"
      className="text-destructive"
      disabled={notified > 0 || deleteMutation.isPending}
      onClick={() => setConfirmOpen(true)}
    >
      {deleteMutation.isPending ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <Trash2 className="mr-2 h-4 w-4" />
      )}
      Eliminar listado
    </Button>
  );

  return (
    <BrowserCompatWrapper>
      {notified > 0 ? (
        <Tooltip>
          {/* Disabled buttons do not fire pointer events; the span keeps the tooltip working */}
          <TooltipTrigger asChild>
            <span tabIndex={0}>{button}</span>
          </TooltipTrigger>
          <TooltipContent>{notifiedReason(notified)}</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar listado municipal</AlertDialogTitle>
            <AlertDialogDescription>{confirmText(list)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirm}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </BrowserCompatWrapper>
  );
};
